import { Flex, Notice, SidebarContent, SidebarPart } from '@eleveo/components-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { useIntegrations } from '../../../app/IntegrationsProvider';
import { TEST_IDS } from '../../../constants/dataTestIds';
import { getSupportedJobs, getSupportedStreams } from '../../../scene/integrations/config';
import {
  WebexCallingConfigurationType
} from '../../../scene/integrations/types';
import { getWebexCallingRootState } from '../../../utils/formUtils';
import { ETL_INTEGRATION_TYPE } from '../../configuration/types';
import { IntegrationTitle } from '../../sideBar/integrations/IntegrationTitle';
import { IntegrationsHeader } from '../../sideBar/integrations/IntegrationsHeader';
import { FieldAccessToken } from '../fields/FieldAccessToken';
import { FieldApiUrl } from '../fields/FieldApiUrl';
import { FieldClientId } from '../fields/FieldClientId';
import { FieldClientSecret } from '../fields/FieldClientSecret';
import { FieldIntegrationName } from '../fields/FieldIntegrationName';
import { FieldOrganizationId } from '../fields/FieldOrganizationId';
import { FieldRefreshToken } from '../fields/FieldRefreshToken';
import { IntegrationJobs } from '../jobs/IntegrationJobs';
import { TeamSelector } from '../jobs/userSync/TeamSelector';
import { TeamSelectorProvider } from '../teamselector/TeamSelectorProvider';

export const WebexCallingForm: React.FC = () => {
  const { t } = useTranslation();

  const { integrationType } = useIntegrations();

  if (integrationType !== ETL_INTEGRATION_TYPE.wxcalling) throw new Error('WebexCallingForm: invalid integrationType!');

  const [isWarningActive, setIsWarningActive] = useState(false);

  const { values: formState } = useFormState<WebexCallingConfigurationType>({ subscription: { values: true } });

  const { wxcallingExpiresIn, wxcallingClientId, wxcallingClientSecret } =
    getWebexCallingRootState(formState);

  useEffect(() => {
    if (wxcallingExpiresIn === undefined) {
      // no authorization, don't show warning
      return;
    }
    setIsWarningActive(true);
  }, [wxcallingClientId, wxcallingClientSecret, wxcallingExpiresIn]);

  const availableJobs = useMemo(() => getSupportedJobs(integrationType), [integrationType]);
  const availableStreams = useMemo(() => getSupportedStreams(integrationType), [integrationType]);

  return (
    <TeamSelectorProvider>
      <IntegrationsHeader />
      <SidebarContent>
        <SidebarPart>
          <Flex column gap={20} className="p-20">
            <IntegrationTitle />
            <FieldIntegrationName />
            <FieldApiUrl />
            <FieldOrganizationId />
            <FieldClientId />
            <FieldClientSecret />
            <FieldAccessToken />
            <FieldRefreshToken />

            {isWarningActive && (
              <Notice color="warning" testId={TEST_IDS.noticeConfigurationWarning}>
                {t('myConfigurations.warning')}
              </Notice>
            )}
            <IntegrationJobs availableJobs={availableJobs} availableStreams={availableStreams} />

          </Flex>
        </SidebarPart>
        <SidebarPart width={600}>
          <TeamSelector />
        </SidebarPart>
      </SidebarContent>
    </TeamSelectorProvider>
  );
};
